import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { notificationsHubService } from '../../services/notificationsHubService';
import Header from '../../components/ui/Header';
import BottomTabNavigation from '../../components/ui/BottomTabNavigation';
import Icon from '../../components/AppIcon';
import CategoryTabs from './components/CategoryTabs';
import NotificationCard from './components/NotificationCard';
import FilterPanel from './components/FilterPanel';
import BulkActionsPanel from './components/BulkActionsPanel';

const NotificationsHub = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [notifications, setNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [activeCategory, setActiveCategory] = useState('all');
  const [filters, setFilters] = useState({
    readStatus: 'all',
    dateFrom: null,
    dateTo: null,
    priority: null
  });
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [realtimeChannel, setRealtimeChannel] = useState(null);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/authentication-screen');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user?.id) {
      loadNotifications();
      loadUnreadCounts();
      setupRealtimeSubscription();
    }

    return () => {
      if (realtimeChannel) {
        notificationsHubService?.unsubscribe(realtimeChannel);
      }
    };
  }, [user]);

  useEffect(() => {
    applyFilters();
  }, [notifications, activeCategory, filters]);

  const translations = {
    en: {
      title: 'Notifications',
      subtitle: 'Stay updated with all your platform activities',
      all: 'All',
      payment: 'Payment Alerts',
      offers: 'Offer Updates',
      flights: 'Flight Delays',
      disputes: 'Disputes',
      admin: 'Admin Messages',
      filters: 'Filters',
      bulkActions: 'Bulk Actions',
      noNotifications: 'No notifications found',
      noNotificationsDesc: 'You\'re all caught up! Check back later for updates.',
      loading: 'Loading notifications...',
      markAllRead: 'Mark All as Read',
      clearOld: 'Clear Old Notifications'
    },
    ko: {
      title: '알림',
      subtitle: '모든 플랫폼 활동에 대한 최신 정보를 받아보세요',
      all: '전체',
      payment: '결제 알림',
      offers: '제안 업데이트',
      flights: '항공편 지연',
      disputes: '분쟁',
      admin: '관리자 메시지',
      filters: '필터',
      bulkActions: '일괄 작업',
      noNotifications: '알림이 없습니다',
      noNotificationsDesc: '모든 알림을 확인했습니다! 나중에 다시 확인해주세요.',
      loading: '알림을 불러오는 중...',
      markAllRead: '모두 읽음으로 표시',
      clearOld: '오래된 알림 삭제'
    }
  };

  const t = translations?.[currentLanguage];

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const data = await notificationsHubService?.getNotifications(user?.id, {
        category: activeCategory,
        ...filters
      });
      setNotifications(data);
    } catch (error) {
      console.error('Load notifications error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCounts = async () => {
    try {
      const counts = await notificationsHubService?.getUnreadCountByCategory(user?.id);
      setUnreadCounts(counts);
    } catch (error) {
      console.error('Load unread counts error:', error);
    }
  };

  const setupRealtimeSubscription = () => {
    const channel = notificationsHubService?.subscribeToNotifications(
      user?.id,
      (newNotification) => {
        setNotifications(prev => [newNotification, ...prev]);
        loadUnreadCounts();
        
        // Play notification sound (optional)
        if ('Notification' in window && Notification?.permission === 'granted') {
          new Notification(newNotification?.title, {
            body: newNotification?.message,
            icon: '/favicon.ico'
          });
        }
      }
    );
    setRealtimeChannel(channel);
  };

  const applyFilters = () => {
    let filtered = [...notifications];

    // Category filter
    if (activeCategory !== 'all') {
      const categoryTypeMap = {
        'payment': 'payment_update',
        'offers': 'deal_status_change',
        'flights': 'system_alert',
        'disputes': 'dispute_filed',
        'admin': 'system_alert'
      };
      
      filtered = filtered?.filter(n => n?.type === categoryTypeMap?.[activeCategory]);
      
      // Additional filter for flights (check metadata)
      if (activeCategory === 'flights') {
        filtered = filtered?.filter(n => 
          n?.metadata?.flightNumber || n?.title?.toLowerCase()?.includes('flight')
        );
      }
      
      // Additional filter for admin messages
      if (activeCategory === 'admin') {
        filtered = filtered?.filter(n => 
          n?.metadata?.isAdminMessage || n?.title?.toLowerCase()?.includes('admin')
        );
      }
    }

    // Read status filter
    if (filters?.readStatus === 'unread') {
      filtered = filtered?.filter(n => !n?.isRead);
    } else if (filters?.readStatus === 'read') {
      filtered = filtered?.filter(n => n?.isRead);
    }

    // Date range filter
    if (filters?.dateFrom) {
      filtered = filtered?.filter(n => new Date(n?.createdAt) >= new Date(filters?.dateFrom));
    }
    if (filters?.dateTo) {
      filtered = filtered?.filter(n => new Date(n?.createdAt) <= new Date(filters?.dateTo));
    }

    // Priority filter
    if (filters?.priority) {
      filtered = filtered?.filter(n => n?.metadata?.priority === filters?.priority);
    }

    setFilteredNotifications(filtered);
  };

  const handleMarkAsRead = async (notificationId) => {
    const success = await notificationsHubService?.markAsRead(notificationId);
    if (success) {
      setNotifications(prev => 
        prev?.map(n => n?.id === notificationId ? { ...n, isRead: true } : n)
      );
      loadUnreadCounts();
    }
  };

  const handleDelete = async (notificationId) => {
    const success = await notificationsHubService?.deleteNotification(notificationId);
    if (success) {
      setNotifications(prev => prev?.filter(n => n?.id !== notificationId));
      loadUnreadCounts();
    }
  };

  const handleMarkAllAsRead = async () => {
    const success = await notificationsHubService?.markAllAsRead(user?.id);
    if (success) {
      setNotifications(prev => prev?.map(n => ({ ...n, isRead: true })));
      loadUnreadCounts();
    }
  };

  const handleClearOld = async () => {
    const success = await notificationsHubService?.deleteOldNotifications(user?.id, 30);
    if (success) {
      loadNotifications();
      loadUnreadCounts();
    }
  };

  const handleBulkMarkAsRead = async () => {
    if (selectedNotifications?.length === 0) return;
    
    const success = await notificationsHubService?.markMultipleAsRead(selectedNotifications);
    if (success) {
      setNotifications(prev => 
        prev?.map(n => selectedNotifications?.includes(n?.id) ? { ...n, isRead: true } : n)
      );
      setSelectedNotifications([]);
      loadUnreadCounts();
    }
  };

  const handleBulkDelete = async () => {
    if (selectedNotifications?.length === 0) return;
    
    const success = await notificationsHubService?.deleteMultiple(selectedNotifications);
    if (success) {
      setNotifications(prev => prev?.filter(n => !selectedNotifications?.includes(n?.id)));
      setSelectedNotifications([]);
      loadUnreadCounts();
    }
  };

  const toggleSelectNotification = (notificationId) => {
    setSelectedNotifications(prev => {
      if (prev?.includes(notificationId)) {
        return prev?.filter(id => id !== notificationId);
      } else {
        return [...prev, notificationId];
      }
    });
  };

  const toggleSelectAll = () => {
    if (selectedNotifications?.length === filteredNotifications?.length) {
      setSelectedNotifications([]);
    } else {
      setSelectedNotifications(filteredNotifications?.map(n => n?.id));
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-20 pb-20 px-4 max-w-6xl mx-auto">
          <div className="flex items-center justify-center py-20">
            <Icon name="Loader2" size={48} className="animate-spin text-primary" />
            <span className="ml-3 text-muted-foreground">{t?.loading}</span>
          </div>
        </div>
        <BottomTabNavigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-20 pb-20 px-4 max-w-6xl mx-auto">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h1 className="text-3xl font-bold text-foreground">{t?.title}</h1>
              <p className="text-muted-foreground mt-1">{t?.subtitle}</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  showFilters
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted hover:bg-muted/80 text-foreground'
                }`}
              >
                <Icon name="Filter" size={18} />
                <span className="text-sm font-medium">{t?.filters}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Category Tabs */}
        <CategoryTabs
          activeCategory={activeCategory}
          onCategoryChange={setActiveCategory}
          unreadCounts={unreadCounts}
          currentLanguage={currentLanguage}
        />

        {/* Filter Panel */}
        {showFilters && (
          <FilterPanel
            filters={filters}
            onFiltersChange={setFilters}
            currentLanguage={currentLanguage}
          />
        )}

        {/* Bulk Actions */}
        {selectedNotifications?.length > 0 && (
          <BulkActionsPanel
            selectedCount={selectedNotifications?.length}
            onMarkAsRead={handleBulkMarkAsRead}
            onDelete={handleBulkDelete}
            onCancel={() => setSelectedNotifications([])}
            currentLanguage={currentLanguage}
          />
        )}

        {/* Quick Actions */}
        {filteredNotifications?.length > 0 && (
          <div className="flex items-center justify-between mb-4 p-3 bg-muted rounded-lg">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={selectedNotifications?.length === filteredNotifications?.length}
                onChange={toggleSelectAll}
                className="w-4 h-4 rounded border-gray-300"
              />
              <span className="text-sm text-muted-foreground">
                {selectedNotifications?.length > 0
                  ? `${selectedNotifications?.length} selected`
                  : 'Select all'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleMarkAllAsRead}
                className="text-sm text-primary hover:text-primary/80 font-medium transition-colors"
              >
                {t?.markAllRead}
              </button>
              <span className="text-muted-foreground">•</span>
              <button
                onClick={handleClearOld}
                className="text-sm text-destructive hover:text-destructive/80 font-medium transition-colors"
              >
                {t?.clearOld}
              </button>
            </div>
          </div>
        )}

        {/* Notifications Feed */}
        <div className="space-y-2">
          {filteredNotifications?.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Bell" size={40} className="text-muted-foreground" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">{t?.noNotifications}</h3>
              <p className="text-muted-foreground">{t?.noNotificationsDesc}</p>
            </div>
          ) : (
            filteredNotifications?.map((notification) => (
              <NotificationCard
                key={notification?.id}
                notification={notification}
                isSelected={selectedNotifications?.includes(notification?.id)}
                onToggleSelect={toggleSelectNotification}
                onMarkAsRead={handleMarkAsRead}
                onDelete={handleDelete}
                currentLanguage={currentLanguage}
              />
            ))
          )}
        </div>
      </main>

      <BottomTabNavigation />
    </div>
  );
};

export default NotificationsHub;